import { CoreBrandData } from "../../components/SettingsPage/types";

/**
 * Builds the open/close handler for a core-brand TreeSelect dropdown. On open it
 * fetches the row's country brand options and seeds the dropdown's temporary
 * selection from whatever value is already saved for that cell.
 *
 * @param params - Collaborators and row context the handler closes over
 * @returns The visibility-change handler
 */
export const createDropdownVisibleChangeHandler = ({
  record,
  i,
  displayCoreBrandsData,
  editingCoreBrandsData,
  updateTreeSelectState,
  getTreeSelectState,
  fetchBrandData,
  getCountryCodeFromMarket,
  authIdClient,
  findBrandByName,
  mergeCompleteSubbrandsToParent,
  brandOptions,
}: {
  record: any;
  i: number;
  displayCoreBrandsData: CoreBrandData[];
  editingCoreBrandsData: { [key: string]: Partial<CoreBrandData> };
  updateTreeSelectState: (fieldKey: string, updates: any) => void;
  getTreeSelectState: (fieldKey: string) => any;
  fetchBrandData: (countryCode: string, authIdClient: string) => void;
  getCountryCodeFromMarket: (market: string) => string;
  authIdClient: string;
  findBrandByName: (brandName: string, brandOptions: any[], subbrandName?: string | null) => { brand: any; subbrand: any; value: string } | null;
  mergeCompleteSubbrandsToParent: (selectedBrands: string[], brandOptions: any[]) => string[];
  brandOptions?: any[];
}) => {
  return (open: boolean) => {
    const fieldKey = `${record.key}_coreBrand${i + 1}`;
    console.log('[onDropdownVisibleChange]', fieldKey, 'open:', open);
    updateTreeSelectState(fieldKey, { isOpen: open });
    
    if (open) {
      const currentDataSource = displayCoreBrandsData.length > 0 ? displayCoreBrandsData : [];
      const currentRecord = currentDataSource.find((item: any) => item.key === record.key);
      
      // Brand options are per-market, so the row's market drives the fetch.
      console.log("[TreeSelect onDropdownVisibleChange] Open dropdown, currentRecord:", currentRecord);
      if (currentRecord && currentRecord.market) {
        const countryCode = getCountryCodeFromMarket(currentRecord.market);
        console.log("[TreeSelect onDropdownVisibleChange] Market:", currentRecord.market, "Country Code:", countryCode);
        fetchBrandData(countryCode, authIdClient);
      } else {
        console.warn("[TreeSelect onDropdownVisibleChange] No market found for record:", record);
      }
      
      const state = getTreeSelectState(fieldKey);
      const currentValue = editingCoreBrandsData[record.key]?.[`coreBrand${i + 1}` as keyof CoreBrandData];
      const currentBrandValue = currentRecord?.[`coreBrand${i + 1}` as keyof CoreBrandData];

      let initialSelectedBrands = state.selectedBrands;

      // Always extract display name from customName if it's stored as JSON
      let initialCustomName = state.customName;
      try {
        const parsedCustomName = JSON.parse(state.customName);
        if (parsedCustomName.displayName) {
          initialCustomName = parsedCustomName.displayName;
        }
      } catch { /* not JSON, use as-is */ }
      
      // Unsaved edits win over the persisted display value. An empty string means the
      // user deliberately cleared the cell, so nothing is restored in that case.
      const valueToInitialize = currentValue === ""
        ? null
        : (currentValue || (currentBrandValue !== "-" && currentBrandValue !== "" ? currentBrandValue : null));

      console.log(`[DROPDOWN_INIT] 🔍 Dropdown initialization for ${fieldKey}:`, {
        currentValue,
        currentBrandValue,
        valueToInitialize,
        stateSelectedBrandsLength: state.selectedBrands.length,
        willCallFindBrandByName: !!(valueToInitialize && state.selectedBrands.length === 0),
        isCleared: currentValue === "",
        brandOptionsLength: brandOptions?.length || 0,
        hasValidBrandOptions: !!(brandOptions && brandOptions.length > 0)
      });

      // Matching requires brandOptions to be loaded. Attempting it against an empty
      // list would resolve nothing and persist a wrongly-empty selection.
      const hasValidBrandOptions = brandOptions &&
        brandOptions.length > 0
      
      if (valueToInitialize && state.selectedBrands.length === 0 && currentValue !== "" && hasValidBrandOptions) {
        try {
          const parsedData = JSON.parse(valueToInitialize);
          if (parsedData.brandData && parsedData.brandData.content) {
            const savedBrands: string[] = [];

            parsedData.brandData.content.forEach((item: any) => {
              const brandName = item.brand;
              const subbrandName = item.subbrand;

              const matchResult = findBrandByName(brandName, brandOptions, subbrandName);

              if (matchResult) {
                savedBrands.push(matchResult.value);
                console.log(`[${fieldKey}] Matched:`, {
                  input: { brand: brandName, subbrand: subbrandName },
                  matched: matchResult.subbrand ?
                    `${matchResult.brand.title} > ${matchResult.subbrand.title}` :
                    matchResult.brand.title,
                  value: matchResult.value
                });
              } else {
                console.warn(`[${fieldKey}] No match found for:`, { brand: brandName, subbrand: subbrandName });
              }
            });

            if (savedBrands.length > 0) {
              // A brand whose every subbrand is selected collapses to the parent node.
              initialSelectedBrands = mergeCompleteSubbrandsToParent(savedBrands, brandOptions);
              console.log(`[${fieldKey}] Restored and merged savedBrands:`, {
                original: savedBrands,
                merged: initialSelectedBrands
              });
            }
            
            if (parsedData.displayName) {
              initialCustomName = parsedData.displayName;
            }
          }
        } catch {
          // Cells saved before the JSON format hold a bare brand id or name.
          const matchResult = findBrandByName(valueToInitialize, brandOptions);

          if (matchResult) {
            initialSelectedBrands = [matchResult.value];
            console.log(`[${fieldKey}] Legacy data matched:`, {
              input: valueToInitialize,
              matched: matchResult.brand.title,
              value: matchResult.value
            });
          } else {
            // An unmatched string is a user-typed custom name, not a brand reference.
            initialCustomName = valueToInitialize;
            console.log(`[${fieldKey}] Legacy data as custom name:`, valueToInitialize);
          }
        }
      } else if (valueToInitialize && state.selectedBrands.length === 0 && currentValue !== "" && !hasValidBrandOptions) {
        // With brandOptions still in flight, set only customName and leave
        // tempSelectedBrands untouched, so the useEffect watching brandOptions can
        // re-run the match once they arrive.
        try {
          const parsedData = JSON.parse(valueToInitialize);
          if (parsedData.displayName) {
            initialCustomName = parsedData.displayName;
            console.log(`[${fieldKey}] ⏳ Brand options not loaded yet, only setting customName: "${initialCustomName}", will re-match when brandOptions loads`);
          }
        } catch {
          initialCustomName = valueToInitialize;
          console.log(`[${fieldKey}] ⏳ Brand options not loaded yet, only setting customName: "${initialCustomName}", will re-match when brandOptions loads`);
        }
      }
      
      updateTreeSelectState(fieldKey, {
        tempSelectedBrands: initialSelectedBrands,
        tempCustomName: initialCustomName
      });
    }
  };
};

